import { Box, Divider, Stack } from '@mui/material';
import { ComponentProps, useRef } from 'react';

import { Markdown } from '@/components/Markdown/Markdown';
import { FlushEditorInner } from '@/components/TextEditor/TextEditor.utils';
import { DocumentMetadata } from '@/modules/trusts/TrustDetails/DocumentMetadata';
import { DocumentType } from '@/types/schema';

import { useDocumentDetails } from '../../contexts/DocumentDetails.context';
import { DocumentDetailsPageContextMenuLazy } from '../../DocumentDetailsPageContextMenuLazy';
import { DocumentSummaryHeader } from '../../DocumentSummaryHeader';
import { RateSummary } from '../../RateSummary';
import { NoSummaryView } from './NoSummaryView';

interface SummaryContentProps {
  documentId: string;
  documentType: DocumentType | null;
  onOpenChatWithSummary?: () => void;
  onGenerateAiSummary: () => void;
  onWriteSummary: () => void;
}

export function SummaryContent({
  documentId,
  documentType,
  onOpenChatWithSummary,
  onGenerateAiSummary,
  onWriteSummary,
}: SummaryContentProps) {
  const { documentSummary } = useDocumentDetails();
  // The context menu drives this summary's editor through DOM events, and the
  // editor's id isn't unique across the page — an open Edit summary trowser mounts
  // a second one. Scope the menu's lookup to this summary's own subtree.
  const summaryRef = useRef<HTMLDivElement>(null);

  if (!documentSummary) {
    return null;
  }

  if (!documentSummary.summary) {
    return (
      <NoSummaryView
        onGenerateAiSummary={onGenerateAiSummary}
        onWriteSummary={onWriteSummary}
      />
    );
  }

  return (
    <Stack flexGrow={1} ref={summaryRef}>
      <Box mb={2}>
        <DocumentSummaryHeader<
          ComponentProps<typeof DocumentDetailsPageContextMenuLazy>
        >
          documentType={documentType ?? null}
          onOpenChatWithSummary={onOpenChatWithSummary}
          slots={{
            ContextMenu: DocumentDetailsPageContextMenuLazy,
            ContextMenuProps: {
              documentId,
              summaryId: documentSummary.id,
              summaryContainerRef: summaryRef,
            },
          }}
        />
      </Box>
      <Markdown
        markdown={documentSummary.summary}
        EditorInner={FlushEditorInner}
        preserveNewLines={true}
      />
      <Stack mt="auto" spacing={2}>
        <Divider />
        <DocumentMetadata<ComponentProps<typeof RateSummary>>
          stackSx={{ p: 0 }}
          documentSummary={documentSummary}
          slots={{
            RateSummary,
            RateSummaryProps: {
              documentSummaryId: documentSummary.id,
              documentSummaryEntryMode: documentSummary.entryMode,
              allowFeedback: !documentSummary.aiSummaryFeedback,
              existingRating: documentSummary.aiSummaryRating ?? null,
            },
          }}
        />
      </Stack>
    </Stack>
  );
}
